/**
 * UnpaidBillsCard - 대시보드 '입금하실 금액' (2026-10-08 대표 지시)
 *
 * 본부가 안내한 청구(알림톡 사용료 · 월 사용료) 중 아직 입금 안 된 것을 한 칸에 모은다.
 *   · 기한 전 = '입금 대기' (주황) / 기한 지남 = '미납' (빨강)
 *   · 낼 것이 없으면 카드 자체를 숨긴다 — 첫 화면에 '없음' 칸을 늘리지 않는다.
 * 금액(부가세 포함)·기한은 서버가 청구서 화면과 같은 계산으로 내려준다(GET /owner/billing/unpaid-summary).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../../../config/api';

interface UnpaidItem {
  kind: string;
  year_month: string;
  total_amount: number;
  due_date: string | null;
  overdue: boolean;
}

interface UnpaidSummary {
  items: UnpaidItem[];
  total_amount: number;
  overdue_count: number;
  payment_account: string | null;
}

const getToken = () =>
  localStorage.getItem('auth_access_token') ||
  localStorage.getItem('accessToken') ||
  localStorage.getItem('token');

const won = (n: number) => `${n.toLocaleString('ko-KR')}원`;
const monthLabel = (ym: string) => `${Number(ym.slice(5, 7))}월분`;
const dueLabel = (d: string | null) => (d ? `${Number(d.slice(5, 7))}/${Number(d.slice(8, 10))}까지` : '');

const UnpaidBillsCard: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<UnpaidSummary | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const token = getToken();
        if (!token) return;
        const res = await fetch(`${API_BASE_URL}/api/v1/owner/billing/unpaid-summary`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        setData(await res.json());
      } catch {
        /* 첫 화면을 막지 않는다 */
      }
    })();
  }, []);

  if (!data || !data.items || data.items.length === 0) return null;

  const overdue = data.overdue_count > 0;
  return (
    <div
      className={`mb-4 rounded-lg border overflow-hidden ${
        overdue ? 'border-red-300 bg-red-50' : 'border-amber-300 bg-amber-50'
      }`}
    >
      <button
        type="button"
        onClick={() => navigate('/owner/usage')}
        className="w-full flex items-center gap-2 px-4 py-3 text-left"
      >
        <span className="flex-shrink-0">💳</span>
        <span className={`font-semibold ${overdue ? 'text-red-900' : 'text-amber-900'}`}>
          {overdue ? '미납 금액' : '입금하실 금액'}
        </span>
        <span className={`font-bold text-lg ${overdue ? 'text-red-700' : 'text-amber-800'}`}>
          {won(data.total_amount)}
        </span>
        <span className="text-xs text-gray-500">(부가세 포함)</span>
        <span className="ml-auto text-sm text-gray-500 flex-shrink-0">청구서 보기 ›</span>
      </button>
      <div className="bg-white border-t border-gray-100">
        {data.items.map((it) => (
          <div
            key={`${it.kind}-${it.year_month}`}
            className="flex items-center gap-2 px-4 py-2 text-sm border-b border-gray-100 last:border-b-0"
          >
            <span className="text-gray-800">
              {it.kind} · {monthLabel(it.year_month)}
            </span>
            <span className="font-medium text-gray-900 ml-auto">{won(it.total_amount)}</span>
            <span
              className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${
                it.overdue ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'
              }`}
            >
              {it.overdue ? `미납 · 기한 지남(${dueLabel(it.due_date)})` : `입금 대기 · ${dueLabel(it.due_date)}`}
            </span>
          </div>
        ))}
        {data.payment_account && (
          <div className="px-4 py-2 text-xs text-gray-500">입금 계좌: {data.payment_account}</div>
        )}
      </div>
    </div>
  );
};

export default UnpaidBillsCard;
